import { useCallback, useEffect, useRef, useState } from 'react';
import { PaymentWebhookFailureService } from '../services/PaymentWebhookFailureService';
import type { PaginationInfo } from '../types';
import type { PaymentWebhookFailure, PaymentWebhookFailureFilters } from '../types/payment';

const SEARCH_DEBOUNCE_MS = 400;

export interface UsePaymentWebhookFailuresReturn {
  rows: PaymentWebhookFailure[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Erro ao carregar falhas de webhook';
};

export function usePaymentWebhookFailures(
  page: number,
  pageSize: number = 10,
  filters: PaymentWebhookFailureFilters = {},
): UsePaymentWebhookFailuresReturn {
  const [rows, setRows] = useState<PaymentWebhookFailure[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousFiltersRef = useRef(filters);

  const fetchPage = useCallback(
    async (
      targetPage: number,
      targetPageSize: number = pageSize,
      serverFilters: PaymentWebhookFailureFilters = {},
    ) => {
      const currentRequestId = ++requestIdRef.current;

      try {
        setLoading(true);
        setError(null);

        const response = await PaymentWebhookFailureService.listPaginated(
          targetPage,
          targetPageSize,
          serverFilters,
        );

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setRows(response.data);
        setPagination(response.pagination);
      } catch (requestError) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setError(getErrorMessage(requestError));
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [pageSize],
  );

  useEffect(() => {
    const onlySearchChanged =
      filters.provider === previousFiltersRef.current.provider
      && filters.status === previousFiltersRef.current.status
      && filters.dateFrom === previousFiltersRef.current.dateFrom
      && filters.dateTo === previousFiltersRef.current.dateTo
      && filters.search !== previousFiltersRef.current.search
      && filters.paymentId === previousFiltersRef.current.paymentId;

    const onlyPaymentIdChanged =
      filters.provider === previousFiltersRef.current.provider
      && filters.status === previousFiltersRef.current.status
      && filters.dateFrom === previousFiltersRef.current.dateFrom
      && filters.dateTo === previousFiltersRef.current.dateTo
      && filters.search === previousFiltersRef.current.search
      && filters.paymentId !== previousFiltersRef.current.paymentId;

    previousFiltersRef.current = filters;

    if (onlySearchChanged || onlyPaymentIdChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, filters);
      }, SEARCH_DEBOUNCE_MS);

      return () => clearTimeout(debounceTimer.current);
    }

    clearTimeout(debounceTimer.current);
    fetchPage(page, pageSize, filters);

    return () => clearTimeout(debounceTimer.current);
  }, [fetchPage, filters, page, pageSize]);

  const refetch = useCallback(() => {
    fetchPage(page, pageSize, filters);
  }, [fetchPage, filters, page, pageSize]);

  return {
    rows,
    pagination,
    loading,
    error,
    refetch,
  };
}
